import * as React from 'react';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import {
  BaseProps,
  ElementOf,
  getClassByTextType,
  TextType,
  tuple,
  ViewIntent,
} from '../common/props';
import { useInverseBg } from '../theme/useInverseBg';

export const TextSize = tuple('normal', 'small');
export type TextSize = ElementOf<typeof TextSize>;

export interface IBaseTextProps extends BaseProps {
  /**
   * Inverzní verze - typicky používáme na tmavším pozadí.
   */
  inverse?: boolean;
  /**
   * Typ informace.
   */
  intent?: ViewIntent;
  /**
   * Typ velikosti textu - doporučované použití - logický typ textu.
   */
  type?: TextType;
  /**
   * Kurzíva.
   */
  italic?: boolean;
  /**
   * Podtržený.
   */
  underline?: boolean;
  /**
   * Tučný.
   */
  strong?: boolean;
  /**
   * Typ velikosti textu - doporučované použití - logický typ textu, vhodné pokud má bý nějaký text inline.
   */
  // type?: TextType;
  /**
   * Pokud není uvedeno, velikost textu se určuje html (tedy jaká je aktální velikost), pokud je uvedeno, bude text zobrazen uvedenou velikostí bez ohledu jak je to v html.
   */
  size?: TextSize;
}

export interface ITextPropsWithIcon extends IBaseTextProps {
  icon?: React.ReactNode;
}

export type TextProps = ITextPropsWithIcon;

/**
 * Komponenta nadpisu.
 */
export const Text = ({
  size,
  className,
  style,
  strong,
  intent,
  italic,
  underline,
  children,
  type,
  icon,
  inverse,
  ...other
}: TextProps) => {
  const parentInverse = useInverseBg(inverse);

  let cls = classNames(
    Classes.TEXT,
    Classes.TYPOGRAPHY,
    className,
    getClassByTextType(type),
    {
      [Classes.TYPOGRAPHY_INHERIT]: !intent || intent === 'inherit',
      [Classes.TYPOGRAPHY_PRIMARY]: intent === 'primary',
      [Classes.TYPOGRAPHY_SECONDARY]: intent === 'secondary',
      [Classes.TYPOGRAPHY_DANGER]: intent === 'danger',
      [Classes.TYPOGRAPHY_SUCCESS]: intent === 'success',
      [Classes.TYPOGRAPHY_WARNING]: intent === 'warning',
      [Classes.TYPOGRAPHY_INACTIVE]: intent === 'inactive',
      [Classes.TYPOGRAPHY_SIZE_INHERIT]: !size,
      [Classes.TYPOGRAPHY_SIZE_NORMAL]: size === 'normal',
      [Classes.TYPOGRAPHY_SIZE_SMALL]: size === 'small',
      [Classes.TYPOGRAPHY_DEFAULT]: intent === 'default',
      [Classes.TYPOGRAPHY_INVERSE]: parentInverse,
    }
  );

  const realContent = (
    <span
      className={classNames({
        [Classes.TYPOGRAPHY_UNDERLINE]: underline,
        [Classes.TYPOGRAPHY_STRONG]: strong,
        [Classes.TYPOGRAPHY_ITALIC]: italic,
      })}
    >
      {children}
    </span>
  );

  return (
    <span {...other} className={cls} style={style}>
      {icon && (
        <span
          className={classNames(Classes.TYPOGRAPHY_ICON, {
            [Classes.TYPOGRAPHY_ICON_SPACE]: !!realContent,
          })}
        >
          {icon}
        </span>
      )}
      {realContent}
    </span>
  );
};
